import { Button } from "@kart/components";
import Table from "rc-table";
import { useEffect, useMemo, useState } from "react";
import ProfileIcon from "../assets/profile.svg?react";
import { useAuth } from "../KeycloakProvider";
import { calculateTimeFromMs } from "../utils/general.utils";
import { getAllSessionsForUser } from "../utils/Session/Session.axios";
import { Session, SessionType } from "../utils/time/time.types";
import { getAllTracksForUser } from "../utils/track/Track.axios";
import { Track } from "../utils/track/Track.types";
import { useAxios } from "../hooks/useAxios";
import { useUser } from "../hooks/useUser";

interface ProfileStatistic {
  sessions: number;
  averagePosition: number;
  bestPosition: number;
  wins: number;
}

export const Profile: React.FC = () => {
  const { keycloak } = useAuth();
  const { axios } = useAxios();
  const { user } = useUser();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);

  useEffect(() => {
    if (!user || !axios) return;
    Promise.all([
      getAllSessionsForUser(axios, user.id),
      getAllTracksForUser(axios, user.id),
    ]).then(([loadedSessions, loadedTracks]) => {
      setSessions(loadedSessions);
      setTracks(loadedTracks);
    });
  }, [axios, user]);

  const displayValues: ProfileStatistic | undefined = useMemo(():
    | ProfileStatistic
    | undefined => {
    if (sessions.length === 0) return;
    return {
      sessions: sessions.length,
      averagePosition:
        Math.round(
          (sessions
            .map((session) => session.position)
            .reduce((pre, cur) => pre + cur, 0) /
            sessions.length) *
            10,
        ) / 10,
      bestPosition:
        sessions.sort((a, b) => a.position - b.position)[0].position || 0,
      wins: sessions.filter((session) => session.position === 1).length,
    };
  }, [sessions]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableHeader = [
    { title: "Nr.", dataIndex: "number", key: "number", width: 10 },
    { title: "Typ", dataIndex: "type", key: "type", width: 10 },
    { title: "Strecke", dataIndex: "track", key: "track", width: 20 },
    { title: "Position", dataIndex: "position", key: "position", width: 20 },
    { title: "Bestzeit", dataIndex: "lapTime", key: "lapTime", width: 40 },
  ];
  const data = useMemo(
    () =>
      sessions.map((session, index) => ({
        number: index + 1,
        track: tracks.find((track) => track.id === session.trackId)?.name,
        position: `${session.position}/${session.competitors}`,
        lapTime: calculateTimeFromMs(session.fastestLap),
        type:
          session.type === SessionType.RACE
            ? "R"
            : session.type === SessionType.QUALI
              ? "Q"
              : "T",
      })),
    [sessions, tracks],
  );
  return (
    <div className="profile-page">
      <div className="profile-page__driver-card__wrapper">
        <ProfileIcon className="profile-page__driver-card__wrapper__icon" />
        <div>
          <p>{`Name: ${user?.username}`}</p>
          <p>{`Tag: ${user?.raceTag}`}</p>
        </div>
      </div>

      <div className="profile-page__statistic">
        <article className="profile-page__statistic__card">
          <div className="profile-page__statistic__card__value">
            {displayValues?.sessions}
          </div>
          <div className="profile-page__statistic__card__label">
            Anzahl Sessions
          </div>
        </article>
        <article className="profile-page__statistic__card">
          <div className="profile-page__statistic__card__value">
            {displayValues?.averagePosition}
          </div>
          <div className="profile-page__statistic__card__label">
            Durschnittsplatzierung
          </div>
        </article>
        <article className="profile-page__statistic__card">
          <div className="profile-page__statistic__card__value">
            {displayValues?.bestPosition}
          </div>
          <div className="profile-page__statistic__card__label">
            Beste Platzierung
          </div>
        </article>
        <article className="profile-page__statistic__card">
          <div className="profile-page__statistic__card__value">
            {displayValues?.wins}
          </div>
          <div className="profile-page__statistic__card__label">Siege</div>
        </article>
      </div>
      <Table className="table" columns={tableHeader} data={data} />
      <Button label="logout" onClick={() => keycloak?.logout()} />
    </div>
  );
};
